import type { GeoJSONGeometry } from '../types';
import {
  isAdminPlaceType,
  isGssOfficialAdminLevel,
  resolveAdminLevel,
} from './contributionPlaceAdmin';

export function isAdminBoundaryPolygon(
  geometry?: GeoJSONGeometry | null,
): boolean {
  return !!geometry && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon');
}

/** Max zoom when fitting map to an admin boundary polygon. */
export function adminBoundaryFitMaxZoom(category?: string | null): number {
  if (category === 'country') return 6;
  if (category === 'region') return 9;
  if (category === 'district') return 11;
  return 11;
}

export function isAdminPointGeometry(
  geometry?: GeoJSONGeometry | null,
): boolean {
  return geometry?.type === 'Point';
}

/** Max zoom when focusing an admin place (polygon fit or point fly). */
export function adminPlaceFitMaxZoom(
  category?: string | null,
  adminLevel?: number | null,
): number {
  const level = resolveAdminLevel(category, adminLevel);
  if (level != null && level <= 2) {
    return adminBoundaryFitMaxZoom(category);
  }
  if (category === 'city') return 13;
  if (category === 'town') return 14;
  if (category === 'village' || category === 'hamlet') return 15;
  if (category === 'suburb' || category === 'quarter') return 15;
  return 14;
}

/** App.tsx handles highlight + map focus when admin place geometry is available. */
export function shouldAppFocusAdminPlace(
  placeType?: string | null,
  geometry?: GeoJSONGeometry | null,
): boolean {
  return isAdminPlaceType(placeType) && !!geometry;
}

export function shouldFitAdminPlacePolygon(
  placeType?: string | null,
  geometry?: GeoJSONGeometry | null,
): boolean {
  return isAdminPlaceType(placeType) && isAdminBoundaryPolygon(geometry);
}

/** Coordal-derived admin code (administrative_boundary.code). */
export function coordalAdminCodeLabel(category?: string | null): string {
  if (category === 'district') return 'District Code';
  if (category === 'region') return 'Region Code';
  if (category === 'country') return 'Country Code';
  if (category === 'city') return 'City Code';
  if (category === 'town') return 'Town Code';
  if (category === 'village') return 'Village Code';
  if (category === 'hamlet') return 'Hamlet Code';
  if (category === 'suburb') return 'Suburb Code';
  if (category === 'quarter') return 'Quarter Code';
  if (category === 'neighbourhood' || category === 'neighborhood' || category === 'locality') {
    return 'Area Code';
  }
  return 'Administrative Code';
}

/** Ghana PostGPS code contributed for an admin place. */
export function ghanaPostGpsAdminCodeLabel(category?: string | null): string {
  if (category === 'district') return 'Ghana PostGPS District Code';
  if (category === 'region') return 'Ghana PostGPS Region Code';
  if (category === 'country') return 'Ghana PostGPS Country Code';
  if (category === 'city') return 'Ghana PostGPS City Code';
  if (category === 'town') return 'Ghana PostGPS Town Code';
  if (category === 'village') return 'Ghana PostGPS Village Code';
  if (category === 'suburb') return 'Ghana PostGPS Suburb Code';
  if (category === 'quarter') return 'Ghana PostGPS Quarter Code';
  if (category === 'neighbourhood' || category === 'neighborhood' || category === 'locality') {
    return 'Ghana PostGPS Area Code';
  }
  return 'Ghana PostGPS Code';
}

/** Official GSS P-code stored at import (administrative_boundary.gss_pcode). Levels 0–2 only. */
export function gssOfficialCodeLabel(category?: string | null): string {
  if (category === 'district') return 'Ghana GSS Official District Code';
  if (category === 'region') return 'Ghana GSS Official Region Code';
  if (category === 'country') return 'Ghana GSS Official Country Code';
  return 'Ghana GSS Official Code';
}

export function showGssOfficialCode(
  category?: string | null,
  adminLevel?: number | null,
  _adminSource?: string | null,
  gssPcode?: string | null,
  coordalCode?: string | null,
): boolean {
  const level = resolveAdminLevel(category, adminLevel);
  if (!isGssOfficialAdminLevel(level)) return false;
  // gss_pcode is populated at GSS import; source may read "user" after metadata contributions.
  if (!gssPcode?.trim()) return false;
  const gss = gssPcode.trim().toUpperCase();
  const coordal = (coordalCode || '').trim().toUpperCase();
  return gss !== coordal;
}
